/* eslint-disable max-len */

"use client"

/* eslint-disable import/no-cycle */
import clsx from "clsx"
import Image from "next/image"
import React, { useEffect, useState } from "react"

import PageGrid from "@/ui/components/PageGrid"
import StatRow from "@/ui/patterns/Stat"

import { InvProps } from "../../staticPages/investments"
import ClientSidePagination from "../pagination/ClientSidePagination"
import PardotForm from "../PardotForm"
import ValuePropsSection from "../use-case/UseCaseValuePropsSection"

import InvestmentCard from "./InvestmentCard"

const stats = [
  {
    statistic: "$600M+",
    description: "Total dollars invested",
    textColor: "whitespace-nowrap",
  },
  {
    statistic: "70+",
    description: "Direct investments",
    textColor: "whitespace-nowrap",
  },
  {
    statistic: "10+",
    description: "LP investments since 2015",
    textColor: "whitespace-nowrap",
  },
]

const custodyProps = {
  heading: "Definitions",
  wrapperClass: "bg-gray-90 !text-gray-2.5",
  lightmode: false,
  valueProps: [
    {
      title: "Markets",
      description:
        "Provides liquidity or enables market participation in the digital assets space through regulated market infrastructure, trading platforms or software offerings",
      image: (
        <Image
          src="/icons/increased.png"
          alt="Icon - Markets"
          className="w-16"
          height={64}
          width={64}
        />
      ),
    },
    {
      title: "Creation",
      description:
        "Facilitates asset creation on-chain, allowing investors, businesses, creators and communities to leverage the inherent efficiencies and digitally-native ownership rights blockchain technology provides",
      image: (
        <Image
          src="/icons/web3.png"
          alt="Icon - Lower Cost"
          className="w-16"
          height={64}
          width={64}
        />
      ),
    },
    {
      title: "Movement",
      description:
        "Enables individuals and companies to transact globally, quickly and securely by leveraging blockchain technology",
      image: (
        <Image
          src="/icons/new-markets.png"
          alt="Icon - Movement"
          className="w-16"
          height={64}
          width={64}
        />
      ),
    },
    {
      title: "Storage",
      description:
        "Empowers retail and institutional investors to safely hold digital assets through regulated custody and software infrastructure solutions",
      image: (
        <Image
          src="/icons/service-layers.png"
          alt="Icon - Storage"
          className="w-16"
          height={64}
          width={64}
        />
      ),
    },
    {
      title: "Funds",
      description:
        "Investment firms specialized in deploying capital within the digital assets space",
      image: (
        <Image
          src="/icons/lower-cost.png"
          alt="Icon - Funds"
          className="w-16"
          height={64}
          width={64}
        />
      ),
    },
  ],
}

const InvestmentsPage: React.FC<InvProps> = ({ data, categories }) => {
  const [activeTab, setActiveTab] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 9

  // Reset currentPage to 1 whenever the activeTab changes
  useEffect(() => {
    setCurrentPage(1)
  }, [activeTab])

  // Determine the investments to show based on the active tab
  let investmentsToShow = []
  if (activeTab === 0) {
    // Show all investments
    investmentsToShow = Object.values(data).flat()
  } else {
    const categoryName = categories[activeTab]
    investmentsToShow = data[categoryName] || []
  }

  // **Sort investmentsToShow alphabetically by company name**
  investmentsToShow.sort((a, b) => a.company.localeCompare(b.company))

  // Calculate total pages
  const totalPages = Math.ceil(investmentsToShow.length / itemsPerPage)

  // Get the investments for the current page
  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentInvestments = investmentsToShow.slice(
    indexOfFirstItem,
    indexOfLastItem,
  )

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  return (
    <div className="bg-gray-90 text-gray-2.5 py-10 md:py-9 lg:py-16 xl:py-28">
      <PageGrid>
        <div className="col-span-full md:col-span-3 lg:col-span-8">
          <h1 className="hidden mb-8 md:block headline5">Ripple Investments</h1>
          <h2 className="xl:headline1 md:headline2 headline3 md:mb-6 ">
            Advancing the Internet of Value through strategic investments
          </h2>
        </div>
      </PageGrid>

      <section className="bg-gray-90 text-gray-2.5">
        <PageGrid className="py-10 md:py-9 lg:py-16 xl:py-28">
          <p className="col-span-full lg:col-span-5 body1">
            Ripple is committed to fostering innovation and growth in the Web
            3.0 space. Our strategic-first approach to investing is focused on
            building and strengthening long-term partnerships with visionary
            teams that are driving market formation and enabling the creation,
            storage, and movement of value.
          </p>
          <div className="flex flex-col p-4 md:flex-wrap lg:flex-nowrap md:flex-row col-span-full lg:col-span-7 lg:col-start-6 text-center">
            {stats.map((stat) => (
              <StatRow
                className="p-4 lg:content-center lg:mr-4 !text-gray-2.5"
                number={stat.statistic}
                description={stat.description}
                key={stat.statistic}
                textColor={stat?.textColor || ""}
                theme="light"
                size="lg"
              />
            ))}
          </div>
        </PageGrid>
      </section>

      {/* Category Tabs */}
      <PageGrid>
        <div
          className="col-span-full flex mb-6 border-b-[1px] border-gray-45 mt-8 overflow-y-hidden overflow-x-scroll scrollbar-hide"
          id="scroll-to-area"
        >
          {categories?.map((cat, index) => (
            <button
              key={cat}
              onClick={() => setActiveTab(index)}
              type="button"
              className={clsx(
                "py-4 px-6 border-b-1 -mb-[1px] mr-4 text-gray-5 whitespace-nowrap",
              )}
              style={{
                borderBottom:
                  activeTab === index
                    ? "3px solid #008CFF"
                    : "1px solid transparent",
              }}
            >
              {cat}
            </button>
          ))}
        </div>
      </PageGrid>

      {/* Investment Cards */}
      <PageGrid>
        {currentInvestments?.map((item) => (
          <InvestmentCard
            key={item.id}
            company={item.company}
            companyLogo={item.companyLogo}
            url={item.url || ""}
            description={item.description}
          />
        ))}
        <div className="col-span-full pt-11 body3 text-gray-50">
          Note: The investments shown are not representative of the full
          portfolio.
        </div>
      </PageGrid>

      {/* Pagination Component */}
      {totalPages > 1 && (
        <div className="pt-16 px-9 md:px-16">
          <ClientSidePagination
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            scrollTo="scroll-to-area"
          />
        </div>
      )}

      <ValuePropsSection {...custodyProps} />

      <section>
        <PageGrid>
          <div className="col-span-8 text-gray-2.5 mb-10">
            <h5 className="headline4 mb-8">Contact the Investment Team</h5>
            <p className="body2">
              Fill out the form to get in touch with a member of our investments
              team. If your organization is a possible fit for investment, we
              will get back to you as soon as we can.
            </p>
          </div>
          <div className="col-span-full">
            <PardotForm
              src="https://go.ripple.com/l/105572/2024-09-09/qt7sr4"
              className="w-full"
            />
          </div>
        </PageGrid>
      </section>
    </div>
  )
}

export default InvestmentsPage
